import { useEffect, useState, useCallback, useRef } from "react";
import axios from "../../config/axios";
import live_display_socket from "../../config/live_display_socket";
import { database } from "../../config/firebaseConfig";
import { useRemote } from "../../hooks/useRemote";
import { useTV } from "../../hooks/useTV";
import { useMusicStreams } from "../../hooks/useMusicStreams";
import useGlobalStore from "../../store/global.store";
import ParkPills from "./ParkPills";
import NewsHero from "./NewsHero";
import ComingUpBoard from "./ComingUpBoard";
import WaitTimesScroll from "./WaitTimesScroll";
import WeatherTile from "./WeatherTile";
import VacationCountdownTile from "./VacationCountdownTile";
import LiveStreamTile from "./LiveStreamTile";
import LiveMusicPlayer from "../Display/LiveMusicPlayer/LiveMusicPlayer";
import { LuVolume1, LuVolume2, LuVolumeOff, LuInfo } from "react-icons/lu";
import { getBackgroundByEST } from "./MultiView/multi-view.utils";

const PARKS_POLL_MS = 60 * 1000;

// LAYOUT MATH
//
// The header sits at the top of the WRAPPER (full screen width). The stage
// below it is fixed-size and scaled to fit the remaining vertical space.
// Because the header is at wrapper level (NOT inside the scaled stage), it's
// always exactly the full screen width on any resolution — no alignment math
// between a "band" and a "scaled header" needed.
//
// DISPLAY 1280x800:
//   header 86px (native) + stage 1280 x 714 (scale 1) = 800 ✓
// TV 1920x1080:
//   header 110px (native, bigger fonts) + stage 1280 x 714 scaled 1.359
//   = 110 + 970 = 1080 ✓, stage width = 1280*1.359 = 1739, side margins
//   = (1920-1739)/2 = ~90px each side (BG color).
const STAGE_W_DISPLAY = 1280;
const STAGE_W_TV = 1360;
const STAGE_H = 714;
const TV_SCALE = 1.359;
const HEADER_H_DISPLAY = 86;
const HEADER_H_TV = 110;
const BG = "#e3e8ef";
const HEADER_NAVY = "#1a3054";
// EXIT button — red with white outline on both display and TV. On TV the
// header also carries the audio-source cycler and the two "switch" buttons;
// arrow keys move focus along the row, Select/OK activates.
const EXIT_RED = "#dc2626";

// ── Shared playback volume (DISPLAY ONLY) ────────────────────────────────
// ONE number drives BOTH audio sources. Previously the music tile defaulted
// to 50 and the park live tile was hard-pinned to 100, so flipping the audio
// source doubled the loudness. Now both are pushed to dashVolume on every
// change, so a switch is level-matched and any volume the user sets carries
// across the flip. TV is untouched — both players stay at 100 there.
const DEFAULT_DASH_VOLUME = 20;
const VOLUME_STEP = 10;

// Audio source cycle: which player is audible.
// Cycle order: MUSIC <-> PARK LIVE STREAM (no mute state)
const AUDIO_MODES = ["music", "live"];
const AUDIO_LABELS = {
  live: "PARK LIVE STREAM",
  music: "MUSIC LIVE STREAM",
};

const Dashboard = ({ device, countDownDate, timezone, currentDate }) => {
  const [subscribedParkKeys, setSubscribedParkKeys] = useState(null);
  const { updateView } = useRemote();
  const { isTv } = useTV();

  // Day/night background — same image + same flip schedule as multiview
  // (getBackgroundByEST, re-checked every 60s).
  const [backgroundImage, setBackgroundImage] = useState(getBackgroundByEST());
  useEffect(() => {
    const interval = setInterval(() => {
      setBackgroundImage(getBackgroundByEST());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Hide the global BottomBar while the Dashboard is open; restore on exit.
  const setShowBottomBar = useGlobalStore((state) => state.setShowBottomBar);
  useEffect(() => {
    setShowBottomBar(false);
    return () => setShowBottomBar(true);
  }, [setShowBottomBar]);

  const fetchParks = useCallback(() => {
    axios
      .get(`/parks/public/${device._id}`, { withCredentials: true })
      .then((res) => {
        setSubscribedParkKeys(res.data.parks.map((p) => p.park_code));
      })
      .catch(() => {});
  }, [device._id]);

  useEffect(() => {
    fetchParks();
  }, [fetchParks]);

  useEffect(() => {
    const id = setInterval(fetchParks, PARKS_POLL_MS);
    return () => clearInterval(id);
  }, [fetchParks]);

  useEffect(() => {
    const onRefresh = () => fetchParks();
    live_display_socket.on("display_refresh", onRefresh);
    return () => {
      live_display_socket.off("display_refresh", onRefresh);
    };
  }, [fetchParks]);

  // ── Music player + live-stream tile control ────────────────────────────
  const { musicStreams } = useMusicStreams();
  const anyMusicLive = Object.values(musicStreams || {}).some(
    (s) => s?.videoId,
  );
  const musicPlayerRef = useRef(null);
  const liveTileRef = useRef(null);

  // Which audio source is audible: "live" | "music" | "none".
  // Persisted across visits; first-ever visit defaults to MUSIC.
  const [audioSource, setAudioSource] = useState(() => {
    const saved = localStorage.getItem("dashAudioSource");
    return AUDIO_MODES.includes(saved) ? saved : "music";
  });

  // Shared volume + mute for whichever source is currently audible.
  const [dashVolume, setDashVolume] = useState(() => {
    const v = parseInt(localStorage.getItem("dashVolume"), 10);
    return Number.isFinite(v)
      ? Math.min(100, Math.max(0, v))
      : DEFAULT_DASH_VOLUME;
  });
  const [dashMuted, setDashMuted] = useState(
    () => localStorage.getItem("dashMuted") === "1",
  );
  
  useEffect(() => {
    // Apply to both players; re-applied shortly after mount so late-loading
    // players land in the right state too.
    const apply = () => {
      // Volume FIRST, and to BOTH players — the inaudible one gets it too,
      // so it is already level-matched the instant the source is flipped.
      // Display only; on TV we leave both players pinned at 100.
      if (!isTv) {
        musicPlayerRef.current?.setVolume?.(dashVolume);
        liveTileRef.current?.setVolume?.(dashVolume);
      }
      // Then routing: only the selected source is audible, and the volume
      // bar's mute button silences whichever one that is.
      musicPlayerRef.current?.setMuted?.(dashMuted || audioSource !== "music");
      liveTileRef.current?.setMuted?.(dashMuted || audioSource !== "live");
    };
    apply();
    localStorage.setItem("dashAudioSource", audioSource);
    if (!isTv) {
      localStorage.setItem("dashVolume", String(dashVolume));
      localStorage.setItem("dashMuted", dashMuted ? "1" : "0");
    }
    const t = setTimeout(apply, 1500);
    return () => clearTimeout(t);
  }, [audioSource, dashVolume, dashMuted, isTv]);

  const cycleAudio = () => {
    setAudioSource(
      (prev) => AUDIO_MODES[(AUDIO_MODES.indexOf(prev) + 1) % AUDIO_MODES.length],
    );
  };

  // Volume bar actions. Raising volume also un-mutes, so a user who hits
  // VOL+ while muted actually hears something.
  const volumeUp = () => {
    setDashMuted(false);
    setDashVolume((v) => Math.min(100, v + VOLUME_STEP));
  };
  const volumeDown = () => {
    setDashVolume((v) => Math.max(0, v - VOLUME_STEP));
  };
  const toggleDashMute = () => setDashMuted((m) => !m);

  // Shared button chrome for the slide-out bar (multiview .control-button).
  const volBtnStyle = (extra = {}) => ({
    background: "#4f5051",
    color: "#fff",
    width: btnH,
    height: btnH,
    borderRadius: 10,
    border: "3px solid #fafbfa",
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    outline: "none",
    userSelect: "none",
    touchAction: "manipulation",
    WebkitTapHighlightColor: "transparent",
    ...extra,
  });

  // ===== Header buttons — TV remote focus row =====
  // TV only: autofocus EXIT on mount. Left/Right walks the header row:
  // [AUDIO] [NEW MUSIC] [CHANGE PARK STREAMER] [EXIT]. Select/OK activates.
  const exitButtonRef = useRef(null);
  const audioBtnRef = useRef(null);
  const newMusicBtnRef = useRef(null);
  const newStreamBtnRef = useRef(null);
  const [focusedBtn, setFocusedBtn] = useState(null);
  useEffect(() => {
    if (!isTv) return;
    const t = setTimeout(() => {
      exitButtonRef.current?.focus();
    }, 100);
    return () => clearTimeout(t);
  }, [isTv]);

  // Header dimensions / fonts — slightly larger on TV.
  const headerH = isTv ? HEADER_H_TV : HEADER_H_DISPLAY;
  const headerPadX = isTv ? 36 : 28;
  const headerSmallFs = isTv ? 30 : 23;
  const headerLargeFs = isTv ? 42 : 32;
  const exitFs = isTv ? 28 : 22;
  const exitPad = isTv ? "12px 36px" : "10px 30px";
  const exitBorderW = isTv ? 4 : 3;
  const ctrlFs = isTv ? 17 : 14;
  // One shared height so every header button matches EXIT exactly.
  const btnH = Math.round(exitFs * 1.2) + (isTv ? 24 : 20) + exitBorderW * 2;

  // Shared handler: header-row d-pad walking.
  const headerRowKeyDown = (refsLeftRight, onSelect) => (e) => {
    if (!isTv) return;
    const [leftRef, rightRef] = refsLeftRight;
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      e.stopPropagation();
      leftRef?.current?.focus();
      return;
    }
    if (e.key === "ArrowRight") {
      e.preventDefault();
      e.stopPropagation();
      rightRef?.current?.focus();
      return;
    }
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      onSelect();
    }
  };

  const ctrlBtnStyle = (name) => ({
    background: "rgba(255,255,255,0.12)",
    color: "#fff",
    border: `${exitBorderW}px solid ${
      isTv && focusedBtn === name ? "#3ADF3C" : "rgba(255,255,255,0.85)"
    }`,
    borderRadius: 12,
    height: btnH,
    boxSizing: "border-box",
    padding: isTv ? "0 18px" : "0 14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    fontFamily: "Lato, sans-serif",
    fontSize: ctrlFs,
    fontWeight: 700,
    cursor: "pointer",
    outline: "none",
    letterSpacing: 0.6,
    whiteSpace: "nowrap",
  });

  return (
    // Outer wrapper — full viewport, BG fills the side margins on TV.
    <div
      style={{
        width: "100vw",
        height: "100vh",
        backgroundColor: BG,
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* HEADER — full screen width, lives outside the scaled stage so it
          ALWAYS reaches both edges on any resolution. */}
      <div
        style={{
          height: headerH,
          background: HEADER_NAVY,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: `0 ${headerPadX}px`,
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span
            style={{
              color: "#fff",
              fontFamily: "Lato, sans-serif",
              fontSize: headerSmallFs,
              fontWeight: 500,
            }}
          >
            Have a Magical Day
          </span>
          <span
            style={{
              color: "#fff",
              fontFamily: "Lato, sans-serif",
              fontSize: headerLargeFs,
              fontWeight: 700,
              lineHeight: 1.1,
            }}
          >
            {device.subscription?.last_name} Family
          </span>
        </div>

        {/* Right side of header: TV control row + EXIT */}
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          {/* Display-only tap-to-switch hint chip */}
          {!isTv && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                background: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.25)",
                borderRadius: 12,
                padding: "10px 16px",
                color: "rgba(255,255,255,0.92)",
                fontFamily: "Lato, sans-serif",
                fontSize: 17,
                fontWeight: 600,
                lineHeight: 1.2,
                maxWidth: 320,
              }}
            >
              <LuInfo
                size={26}
                style={{ flexShrink: 0, opacity: 0.9 }}
              />
              <span>TIP: Tap the music or park live players to switch streamers</span>
            </div>
          )}
          {/* Audio source cycler — both devices (tap on display, d-pad on TV) */}
          <button
            ref={audioBtnRef}
            onClick={cycleAudio}
            onFocus={() => isTv && setFocusedBtn("audio")}
            onBlur={() => isTv && setFocusedBtn(null)}
            onKeyDown={
              isTv
                ? headerRowKeyDown([null, newMusicBtnRef], cycleAudio)
                : undefined
            }
            tabIndex={isTv ? 0 : -1}
            style={ctrlBtnStyle("audio")}
          >
                <LuVolume2 size={26} />
                <span style={{ textAlign: "left", lineHeight: 1.15 }}>
                  SWITCH AUDIO SOURCE
                  <br />
                  <span style={{ fontSize: ctrlFs - 3, opacity: 0.85 }}>
                    {AUDIO_LABELS[audioSource]}
                  </span>
                </span>
          </button>
          {isTv && (
            <>
              {anyMusicLive && (
                <button
                  ref={newMusicBtnRef}
                  onClick={() => musicPlayerRef.current?.switchRandom?.()}
                  onFocus={() => setFocusedBtn("newmusic")}
                  onBlur={() => setFocusedBtn(null)}
                  onKeyDown={headerRowKeyDown(
                    [audioBtnRef, newStreamBtnRef],
                    () => musicPlayerRef.current?.switchRandom?.(),
                  )}
                  tabIndex={0}
                  style={ctrlBtnStyle("newmusic")}
                >
                  CHANGE MUSIC STREAMER
                </button>
              )}
              <button
                ref={newStreamBtnRef}
                onClick={() => liveTileRef.current?.switchRandom?.()}
                onFocus={() => setFocusedBtn("newstream")}
                onBlur={() => setFocusedBtn(null)}
                onKeyDown={headerRowKeyDown(
                  [anyMusicLive ? newMusicBtnRef : audioBtnRef, exitButtonRef],
                  () => liveTileRef.current?.switchRandom?.(),
                )}
                tabIndex={0}
                style={ctrlBtnStyle("newstream")}
              >
                CHANGE PARK STREAMER
              </button>
            </>
          )}
          {/* Volume controls — display only, always visible, tucked right
              up against EXIT. Own tight gap (6) so the three buttons read
              as one cluster instead of three separate header items. */}
          {!isTv && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button onClick={volumeDown} tabIndex={-1} style={volBtnStyle()}>
                <LuVolume1 size={28} />
              </button>
              <button onClick={volumeUp} tabIndex={-1} style={volBtnStyle()}>
                <LuVolume2 size={28} />
              </button>
              <button
                onClick={toggleDashMute}
                tabIndex={-1}
                style={volBtnStyle({
                  borderColor: dashMuted ? "red" : "#05f651",
                  color: dashMuted ? "red" : "#05f651",
                })}
              >
                <LuVolumeOff size={28} />
              </button>
            </div>
          )}
          <button
            ref={exitButtonRef}
            onClick={() => updateView("Home")}
            onFocus={() => setFocusedBtn("exit")}
            onBlur={() => setFocusedBtn(null)}
            onKeyDown={(e) => {
              if (!isTv) return;
              if (e.key === "ArrowLeft") {
                e.preventDefault();
                e.stopPropagation();
                newStreamBtnRef.current?.focus();
                return;
              }
              if (
                e.key === "ArrowUp" ||
                e.key === "ArrowDown" ||
                e.key === "ArrowRight"
              ) {
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                updateView("Home");
              }
            }}
            tabIndex={0}
            style={{
              background: EXIT_RED,
              color: "#fff",
              border: `${exitBorderW}px solid ${
                isTv && focusedBtn === "exit" ? "#3ADF3C" : "#fff"
              }`,
              borderRadius: 12,
              height: btnH,
              boxSizing: "border-box",
              padding: isTv ? "0 36px" : "0 30px",
              display: "flex",
              alignItems: "center",
              fontFamily: "Lato, sans-serif",
              fontSize: exitFs,
              fontWeight: 700,
              cursor: "pointer",
              outline: "none",
              letterSpacing: 1,
            }}
          >
            EXIT
          </button>
        </div>
      </div>

      {/* STAGE AREA — flexes to fill the rest of the screen, centers the
          fixed-size stage. On TV the stage is scaled; side margins show BG. */}
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {/* Fixed 1280x714 stage. On display: scale 1. On TV: scale 1.359
            to fill the available height (970px on a 1080 TV). */}
        <div
          style={{
            width: isTv ? STAGE_W_TV : STAGE_W_DISPLAY,
            height: STAGE_H,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            transform: isTv ? `scale(${TV_SCALE})` : "scale(1)",
            transformOrigin: "center center",
          }}
        >
          {/* Park Pills Row */}
          <ParkPills
            database={database}
            subscribedParkKeys={subscribedParkKeys}
            style={{ padding: "12px 16px", flexShrink: 0 }}
          />

          {/* Content area */}
          <div
            style={{
              flex: 1,
              display: "flex",
              gap: 14,
              padding: "0 16px 16px",
              minHeight: 0,
            }}
          >
            {/* Left column */}
            <div
              style={{
                flex: "0 0 58%",
                display: "flex",
                flexDirection: "column",
                gap: 14,
                minHeight: 0,
              }}
            >
              <div style={{ height: 320, flexShrink: 0 }}>
                <NewsHero database={database} />
              </div>
              <div style={{ flex: 1, minHeight: 0 }}>
                <ComingUpBoard
                  database={database}
                  subscribedParkKeys={subscribedParkKeys}
                />
              </div>
            </div>

            {/* Right column */}
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 14,
                minHeight: 0,
              }}
            >
              {/* Wait times narrowed; live music player sits beside it as a
                  small tile. When no music is live the player renders null
                  and the scroll takes the full width again. */}
              <div
                style={{
                  display: "flex",
                  gap: 14,
                  height: 190,
                  flexShrink: 0,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <WaitTimesScroll
                    database={database}
                    subscribedParkKeys={subscribedParkKeys}
                  />
                </div>
                {/* NEVER conditionally mount the player: anyMusicLive comes
                    from a live Firebase subscription, and a single false
                    reading destroys the iframe and restarts playback (the
                    "spins, plays, stops" stutter). The component already
                    renders null when nothing is live, so we only collapse
                    the wrapper's width — matching PhotoFrame, which mounts
                    it unconditionally and plays smoothly. */}
                <div
                  style={{
                    width: anyMusicLive ? 170 : 0,
                    flexShrink: 0,
                    overflow: "hidden",
                  }}
                >
                  <LiveMusicPlayer
                    ref={musicPlayerRef}
                    variant="tile"
                    upRef={exitButtonRef}
                  />
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 14,
                  height: 130,
                  flexShrink: 0,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <VacationCountdownTile
                    countDownDate={countDownDate}
                    timezone={timezone}
                    currentDate={currentDate}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <WeatherTile
                    database={database}
                    subscribedParkKeys={subscribedParkKeys}
                  />
                </div>
              </div>

              <div style={{ flex: 1, minHeight: 0 }}>
                <LiveStreamTile ref={liveTileRef} timezone={timezone} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
